import Vue from "vue"
import { amountStringIsRounded } from "@/helpers/PaymentTypeHelpers"

// This function should only be used for currency amounts, not quantities
function processCurrencyAmountForCountsData(amount) {
    // Ensure value is a string and trim only redundant leading zeros
    let amountStr = String(amount)
    // If the value starts with '0' and the next char is not ',' or end, trim all but one zero
    if (/^0{2,}/.test(amountStr)) {
        // More than one leading zero, keep only one
        amountStr = amountStr.replace(/^0+/, "0")
    } else if (/^0[1-9]/.test(amountStr)) {
        // If it's like '01', '02', etc., trim the leading zero
        amountStr = amountStr.replace(/^0+/, "")
    }

    return amountStr
}

function convertToCents(amountStr) {
    if (amountStr === "" || amountStr === null || amountStr === undefined) {
        return 0
    }

    let normalized = String(amountStr).replace(",", ".")
    //return Math.round(parseFloat(normalized) * 100)
    return normalized * 100
}

function minimumValidationResult(validationStatuses) {
    if (validationStatuses.includes("invalid")) {
        return "invalid"
    } else if (validationStatuses.includes("empty")) {
        return "empty"
    } else {
        return "valid"
    }
}

function getValidationStatusForCount(count, paymentType) {
    if (count === "") {
        return "empty"
    }

    if (paymentType.interface.key === "cash" && paymentType.count_type === "total_only") {
        return amountStringIsRounded(count, paymentType.rounding) ? "valid" : "invalid"
    }

    return "valid"
}

const getDefaultState = () => {
    return {
        openZReport: false,
        zReportActionRequired: false,
        unintegratedPaymentTypes: [],
        transferToDeposit: [],
        transferToDepositViewActive: false,

        counts: false,
        //countsTotals: [ ],
        currentPaymentTypeIndex: false,
        currentDenominationIndex: false,
        nextPreventedAtIndex: false,
        unintegratedIndexMapping: {},
        integratedIndexMapping: {},
        finalInputCoordinate: false,
        mustFill: false,
        //finishedFilling: false,

        isProcessingSubmitZReportRequest: false,
        isProcessingGetZReportRequest: false,
        isWaitingForRedirect: false,

        isAwaitingConfirmations: {
            difference: false,
            doNotTransferToCashDeposit: false,
            receivedFromCashDepositSinceClose: false,
            transferredToCashDepositSinceClose: false,
        },

        transferredToCashDepositSinceLastClose: false,
        //isAwaitingDifferenceConfirmation: false,
    }
}

export default {
    state: getDefaultState,

    getters: {
        unintegratedCashReportPaymentTypes: (state) => {
            return state.unintegratedPaymentTypes
        },

        currentPaymentTypeCounts: (state) => {
            return state.counts[state.currentPaymentTypeIndex]
        },

        counts: (state) => {
            return state.counts
        },

        indexOfCashPaymentType: (state) => {
            return state.unintegratedPaymentTypes.findIndex((paymentType) => paymentType.interface.key === "cash")
        },

        currentPaymentTypeTransferToDeposit: (state) => {
            return state.transferToDeposit[state.currentPaymentTypeIndex]
        },

        // Okay, not really a clean solution we're continuing on here... maybe refactor
        transferToDepositOfUnintegratedPaymentTypeAtIndex: (state) => (index) => {
            return state.transferToDeposit[index]
        },

        mustFill: (state) => {
            return state.mustFill
        },

        nextFieldDoesntExist: (state) => {
            return (
                state.finalInputCoordinate &&
                state.finalInputCoordinate[0] === state.currentPaymentTypeIndex &&
                state.finalInputCoordinate[1] === state.currentDenominationIndex
            )
        },

        currentPaymentTypeIndex: (state) => {
            return state.currentPaymentTypeIndex
        },

        currentPaymentType: (state) => {
            return state.unintegratedPaymentTypes[state.currentPaymentTypeIndex]
        },

        currentDenominationIndex: (state) => {
            return state.currentDenominationIndex
        },

        nextPreventedAtIndex: (state) => {
            return state.nextPreventedAtIndex
        },

        nextPreventedAtGivenIndex: (state) => (index) => {
            return state.nextPreventedAtIndex === index
        },

        currentPaymentTypeCountValue: (state) => {
            if (state.currentPaymentTypeIndex === false || state.currentDenominationIndex === false) {
                return null
            }
            /* console.log("currentPaymentTypeIndex", state.currentPaymentTypeIndex)
            console.log("currentDenominationIndex", state.currentDenominationIndex) */
            return state.counts[state.currentPaymentTypeIndex][state.currentDenominationIndex]
        },

        currentPaymentTypeClass: (state) => {
            return state.unintegratedPaymentTypes.length === 0 ||
                !(state.currentPaymentTypeIndex in state.unintegratedPaymentTypes)
                ? false
                : state.unintegratedPaymentTypes[state.currentPaymentTypeIndex].interface.key
        },

        everyZReportElementDisabled: (state) => {
            return (
                state.isProcessingSubmitZReportRequest ||
                state.isProcessingGetZReportRequest ||
                state.isWaitingForRedirect
            )
        },

        hasFinishedFilling: (state, getters) => {
            return getters.unintegratedCashReportPaymentTypes
                .map((paymentType, index) => getters.minimumValidationResultForPaymentType(index))
                .every((status) => status === "valid")
        },

        isAwaitingConfirmationOfType: (state) => (type) => {
            // Equally legitimate values: undefined, false, and true
            return state.isAwaitingConfirmations[type] !== false
        },

        isAwaitingAnyConfirmations: (state) => {
            return Object.values(state.isAwaitingConfirmations).some((v) => v !== false)
        },

        valueOfAwaitingConfirmationType: (state) => (type) => {
            return state.isAwaitingConfirmations[type]
        },

        // Gets the value of the open count of the (integrated or unintegrated) payment type at index
        paymentTypeOpenValue: (state) => (absIndex) => {
            return state.openZReport.statement[absIndex].counts.open.value
        },

        openDifferenceOfUnintegratedPaymentTypeAtIndex: (state) => (index) => {
            return state.unintegratedPaymentTypes[index]
        },

        // Gets the accounted total of the (integrated or unintegrated) payment type at index
        paymentTypeTotal: (state) => (absIndex) => {
            return state.openZReport && state.openZReport.statement[absIndex]
                ? state.openZReport.statement[absIndex].total
                : 0
        },

        // Gets the current count value of the unintegrated payment type at index
        // relIndex is index of the current `count` field being selected-processed, integer non negative
        paymentTypeCountTotal: (state) => (relIndex) => {
            const typeHasClass = state.unintegratedPaymentTypes[relIndex].interface.key
            if (typeHasClass === "cash") {
                // hardcoded array of krona banknote nominals in cash count: 50, 100, 200, ...
                let units = state.unintegratedPaymentTypes[relIndex].counts_format.units

                if (units === null) {
                    if (state.counts[relIndex][0] === undefined) {
                        console.error("paymentTypeCountTotal: total-only counts but no value found")
                        return 0
                    }

                    return convertToCents(state.counts[relIndex][0])
                }

                // state.counts is array of user-inputed values in cash count, usually all 0 when register is being opened
                // result is calculated by "reducing" array to one number. Any empty field is considered to be 0 and ignored
                return state.counts[relIndex].reduce((previous, current, currentCountIndex) => {
                    let mutatedCurrent = current === "" || current === undefined || current === null ? 0 : current
                    return previous + mutatedCurrent * units[currentCountIndex]
                }, 0)
            } else if (["autonomous_terminal", "enclosure"].includes(typeHasClass)) {
                return state.counts[relIndex].reduce((previous, current) => {
                    return previous + convertToCents(current)
                }, 0)
            }
        },

        // copy of paymentTypeCountTotal which returns NaN for empty or undefined fields so we know not all fields were initialized with numeric values
        paymentTypeCountTotalOrNaN: (state) => (relIndex) => {
            const typeHasClass = state.unintegratedPaymentTypes[relIndex].interface.key
            if (typeHasClass === "cash") {
                // hardcoded array of krona banknote nominals in cash count: 50, 100, 200, ...
                let units = state.unintegratedPaymentTypes[relIndex]["counts_format"]["units"]

                // result is calculated by "reducing" array to one number. If any field is empty NaN returned
                return state.counts[relIndex].reduce((previous, current, currentCountIndex) => {
                    let mutatedCurrent = current === "" || current === undefined || current === null ? NaN : current
                    return previous + mutatedCurrent * units[currentCountIndex]
                }, 0)
            } else if (["autonomous_terminal", "enclosure"].includes(typeHasClass)) {
                return state.counts[relIndex].reduce((previous, current) => {
                    let mutatedCurrent =
                        current === "" || current === undefined || current === null ? NaN : convertToCents(current)
                    return previous + mutatedCurrent
                }, 0)
            }
        },

        paymentTypeTransferTotal: (state) => (relIndex) => {
            const typeHasClass = state.unintegratedPaymentTypes[relIndex].interface.key
            if (typeHasClass === "cash") {
                let units = state.unintegratedPaymentTypes[relIndex]["counts_format"]["units"]

                if (units === null) {
                    // Total-only counts
                    return convertToCents(state.transferToDeposit[relIndex][0])
                }

                return state.transferToDeposit[relIndex].reduce((previous, current, currentCountIndex) => {
                    return previous + current * units[currentCountIndex]
                }, 0)
            }

            // Should never get here because it's always cash
            return 0
        },

        paymentTypeRemainsInRegister: (state, getters) => (relIndex) => {
            return getters.paymentTypeCountTotal(relIndex) - getters.paymentTypeTransferTotal(relIndex)
        },

        paymentTypeDifference: (state, getters) => (relIndex) => {
            return (
                Math.round(getters.paymentTypeCountTotal(relIndex)) -
                getters.paymentTypeTotal(state.unintegratedIndexMapping[relIndex]) -
                getters.paymentTypeOpenValue(state.unintegratedIndexMapping[relIndex])
            )
        },

        totalDifference: (state, getters) => {
            let totalDifference = 0

            if (state.zReportActionRequired === "close") {
                for (let i = 0; i < state.counts.length; i++) {
                    const difference = getters.paymentTypeDifference(i)
                    totalDifference += difference
                }
            }

            return totalDifference
        },

        zReportActionRequired: (state) => {
            return state.zReportActionRequired
        },

        zReportActionRequiredIsOpen: (state) => {
            return "open" == state.zReportActionRequired
        },

        countsValidationStatuses: (state, getters) => {
            if (!state.counts) {
                return null
            }

            return state.counts.map((paymentTypeCounts, paymentTypeIndex) => {
                const paymentType = state.unintegratedPaymentTypes[paymentTypeIndex]
                const interfaceKey = paymentType.interface.key
                //const countFormat = paymentType.counts_format

                return paymentTypeCounts.map((count, countIndex) => getValidationStatusForCount(count, paymentType))
            })
        },

        validationStatusForCountAtIndices: (state, getters) => (indices) => {
            const paymentType = state.unintegratedPaymentTypes[indices[0]]
            const count = state.counts[indices[0]][indices[1]]

            return getValidationStatusForCount(count, paymentType)
        },

        // ugh, messy
        validationStatusForTotalOnlyTransferCountAtPaymentTypeIndex: (state, getters) => (paymentTypeIndex) => {
            const paymentType = state.unintegratedPaymentTypes[paymentTypeIndex]
            if (paymentType.interface.key !== "cash" || paymentType.count_type !== "total_only") {
                console.error(
                    "validationStatusForTotalOnlyTransferCountAtPaymentTypeIndex called for non-total-only cash payment type"
                )
                return null
            }

            if (getters.paymentTypeRemainsInRegister(paymentTypeIndex) < 0) {
                return "invalid"
            }

            const count = state.transferToDeposit[paymentTypeIndex][0]
            return getValidationStatusForCount(count, paymentType)
        },

        minimumValidationResultForPaymentType: (state, getters) => (paymentTypeIndex) => {
            if (!getters.countsValidationStatuses) {
                return "empty"
            }

            const statuses = getters.countsValidationStatuses[paymentTypeIndex].slice() // Copy to avoid problems

            const paymentType = state.unintegratedPaymentTypes[paymentTypeIndex]
            if (
                paymentType.interface.key === "cash" &&
                paymentType.count_type === "total_only" &&
                state.zReportActionRequired === "close"
            ) {
                statuses.push(getters.validationStatusForTotalOnlyTransferCountAtPaymentTypeIndex(paymentTypeIndex))
            }

            return minimumValidationResult(statuses)
        },

        previousCloseCountDataOfCurrentPaymentType: (state, getters) => {
            if (!getters.register.previous_cash_close) {
                return null
            }

            const previous = getters.register.previous_cash_close.find(
                (c) => c.payment_type_uuid === getters.currentPaymentType.uuid
            )

            if (previous === undefined) {
                return null
            }

            return previous.amounts
        },

        inputDisabledForCurrentPaymentType: (state, getters) => {
            if (!getters.currentPaymentType) {
                return true
            }

            if (
                getters.zReportActionRequired === "open" &&
                ["autonomous_terminal", "enclosure"].includes(getters.currentPaymentType.interface.key)
            ) {
                return true
            }

            return false
        },

        nextCounts: (state, getters) => {
            return getters.counts.slice(state.currentPaymentTypeIndex + 1)
        },

        currentPaymentTypeIsFinal: (state, getters) => {
            return state.currentPaymentTypeIndex === getters.counts.length - 1
        },

        indexOfNextUnfilledPaymentType: (state, getters) => {
            return getters.counts.findIndex(
                (counts, index) => index > state.currentPaymentTypeIndex && counts.some((count) => count === "")
            )
        },

        someNextUnfilledPaymentType: (state, getters) => {
            return getters.indexOfNextUnfilledPaymentType !== -1
        },

        currentPaymentTypeCountIsAllZero: (state, getters) => {
            return getters.currentPaymentTypeCounts.every((c) => c === "0")
        },

        currentPaymentTypeIsCashUsingTotalOnlyCounts: (state, getters) => {
            return (
                getters.currentPaymentType &&
                getters.currentPaymentType.interface.key === "cash" &&
                getters.currentPaymentType.counts_format.units === null
            )
        },

        currentPaymentTypeExpectsCurrencyAmountInput: (state, getters) => {
            return (
                getters.currentPaymentType &&
                (["autonomous_terminal", "enclosure"].includes(getters.currentPaymentType.interface.key) ||
                    getters.currentPaymentTypeIsCashUsingTotalOnlyCounts)
            )
        },

        transferToDepositViewActiveAtIndex: (state) => (index) => {
            return state.transferToDepositViewActive[index] === true
        },
    },

    mutations: {
        setOpenZReport(state, zReport) {
            state.openZReport = zReport
        },

        setZReportActionRequired(state, action) {
            state.zReportActionRequired = action
            state.mustFill = action === "open" ? ["cash"] : ["cash", "autonomous_terminal", "enclosure"]
        },

        resetCashReportState(state) {
            Object.assign(state, getDefaultState())
        },

        setUnintegratedPaymentTypes(state, unintegratedPaymentTypes) {
            state.unintegratedPaymentTypes = unintegratedPaymentTypes
        },

        setTransferToDepositFalse(state, paymentTypeIndex) {
            state.transferToDeposit[paymentTypeIndex].fill(0)
            //Vue.set(state.transferToDeposit, paymentTypeIndex, false);
        },

        setFinalInputCoordinate(state, coordinate) {
            state.finalInputCoordinate = coordinate
        },

        nextIndex(state) {
            //const countsFormatIsntNull = state.unintegratedPaymentTypes[state.currentPaymentTypeIndex]['counts_format']['units'] !== null;
            const findPaymentTypeToFill = () => {
                for (let i = state.currentPaymentTypeIndex + 1; i < state.unintegratedPaymentTypes.length; i++) {
                    if (state.mustFill.includes(state.unintegratedPaymentTypes[i].interface.key)) {
                        state.currentPaymentTypeIndex = i
                        state.currentDenominationIndex = 0
                        return true
                    }
                }
                return false
            }

            if (state.currentDenominationIndex === "transferTotal") {
                findPaymentTypeToFill()
                return
            }

            const nextDenominationIsDefined =
                state.unintegratedPaymentTypes[state.currentPaymentTypeIndex]["counts_format"]["units"]?.[
                    state.currentDenominationIndex + 1
                ] !== undefined

            if (nextDenominationIsDefined) {
                state.currentDenominationIndex++
                return
            }

            const nextIsTransferToDepositTotalForTotalOnlyCounts =
                state.zReportActionRequired === "close" &&
                state.currentDenominationIndex === 0 &&
                state.unintegratedPaymentTypes[state.currentPaymentTypeIndex].interface.key === "cash" &&
                state.unintegratedPaymentTypes[state.currentPaymentTypeIndex].count_type === "total_only"
            if (nextIsTransferToDepositTotalForTotalOnlyCounts) {
                //console.log("next is transfer to deposit total for total-only counts")
                state.currentDenominationIndex = "transferTotal"
                return
            }

            findPaymentTypeToFill()
        },

        setPaymentTypeIndex(state, paymentTypeIndex) {
            state.currentDenominationIndex = 0
            state.currentPaymentTypeIndex = paymentTypeIndex
        },

        setDenominationIndex(state, denominationIndex) {
            state.currentDenominationIndex = denominationIndex
        },

        setNextPreventedAtIndex(state, index) {
            state.nextPreventedAtIndex = index
        },

        setInitialCurrentIndices(state) {
            const firstPaymentTypeIndex = state.unintegratedPaymentTypes.findIndex((p) =>
                state.mustFill.includes(p.interface.key)
            )

            if (firstPaymentTypeIndex !== -1) {
                state.currentPaymentTypeIndex = firstPaymentTypeIndex
                state.currentDenominationIndex = 0
            }
        },

        setIsProcessingSubmitRequest(state, value) {
            state.isProcessingSubmitZReportRequest = value
        },

        setIsProcessingGetRequest(state, value) {
            state.isProcessingGetRequest = value
        },

        setIsWaitingForRedirect(state, value) {
            state.isWaitingForRedirect = value
        },

        setIsAwaitingConfirmation(state, data) {
            Vue.set(state.isAwaitingConfirmations, data.type, data.value)
        },

        setIsAwaitingDifferenceConfirmation(state, value) {
            Vue.set(state.isAwaitingConfirmations, "difference", value)
        },

        // Used to ask cashiers for confirmation that they don't want to register the transfer of any cash to the deposit.
        setIsAwaitingDoNotTransferToCashDepositConfirmation(state, value) {
            Vue.set(state.isAwaitingConfirmations, "doNotTransferToCashDeposit", value)
        },

        // Used to ask the cashier whose opening cash count does not match the previous cash closing count whether the
        // difference is the consequence of someone transferring currency from the cash deposit to the register while the
        // register was closed
        setIsAwaitingReceivedFromCashDepositConfirmation(state, value) {
            Vue.set(state.isAwaitingConfirmations, "receivedFromCashDepositSinceClose", value)
        },

        setIsAwaitingTransferredToCashDepositConfirmation(state, value) {
            Vue.set(state.isAwaitingConfirmations, "transferredToCashDepositSinceClose", value)
        },

        setIsAwaitingNoConfirmations(state) {
            for (const [key, value] of Object.entries(state.isAwaitingConfirmations)) {
                Vue.set(state.isAwaitingConfirmations, key, false)
            }
        },

        setUnintegratedIndexMapping(state, mapping) {
            state.unintegratedIndexMapping = mapping
        },

        setIntegratedIndexMapping(state, mapping) {
            state.integratedIndexMapping = mapping
        },

        /* setFinishedFilling (state, value) {
            state.finishedFilling = value;
        }, */

        setAllCounts(state, value) {
            state.counts = value

            state.transferToDeposit = Array(state.unintegratedPaymentTypes.length).fill(false)
            state.unintegratedPaymentTypes.forEach((p, index) => {
                if (p.interface.key === "cash" && state.zReportActionRequired === "close") {
                    let transferCounts = Array(state.counts[index].length)

                    let fillValue
                    if (state.counts[index].length === 1 && p.counts_format.units === null) {
                        // Total-only counts: We will force them to fill the total amount to transfer
                        fillValue = ""
                    } else {
                        // Regular counts
                        fillValue = 0
                    }

                    transferCounts.fill(fillValue)

                    Vue.set(state.transferToDeposit, index, transferCounts)
                }
            })
        },

        setPaymentTypeCounts(state, { paymentTypeIndex, countsArray }) {
            Vue.set(state.counts, paymentTypeIndex, countsArray)
        },

        setCountValue(state, value) {
            Vue.set(
                state.counts[state.currentPaymentTypeIndex],
                state.currentDenominationIndex,
                processCurrencyAmountForCountsData(value)
            )
        },

        zeroFillAllCounts(state) {
            state.counts.forEach((paymentTypeCounts, index, allCounts) => {
                //state.counts[index].fill('0');
                Vue.set(state.counts, index, paymentTypeCounts.fill("0"))
            })

            state.transferToDeposit.forEach((paymentTypeTransfers, index, allCounts) => {
                //state.transferToDeposit[index].fill(0);
                if (paymentTypeTransfers === false) {
                    return
                }

                if (
                    paymentTypeTransfers.length === 1 &&
                    state.unintegratedPaymentTypes[index].count_type === "total_only"
                ) {
                    // Total-only counts need a string :/
                    Vue.set(state.transferToDeposit, index, paymentTypeTransfers.fill("0"))
                } else {
                    Vue.set(state.transferToDeposit, index, paymentTypeTransfers.fill(0))
                }
            })
        },

        moveQuantityAtDenominationIndex(state, { index, qty }) {
            //Vue.set(state.counts[state.currentPaymentTypeIndex], index, state.counts[state.currentPaymentTypeIndex][index] - qty);
            Vue.set(
                state.transferToDeposit[state.currentPaymentTypeIndex],
                index,
                state.transferToDeposit[state.currentPaymentTypeIndex][index] + qty
            )
        },

        setCurrentPaymentTypeTransferToDeposit(state, transferValue) {
            if (
                state.currentPaymentTypeIndex === false ||
                state.transferToDeposit[state.currentPaymentTypeIndex] === false
            ) {
                console.error(
                    "Attempted to set total transfer value with no current payment type or transferToDeposit false"
                )
                return
            }

            if (state.transferToDeposit[state.currentPaymentTypeIndex].length !== 1) {
                console.error("Attempted to set total transfer value on non-total-only counts")
                return
            }

            // Ensure value is a string and trim leading zeros (but leave a single zero if all digits are zero)
            Vue.set(
                state.transferToDeposit[state.currentPaymentTypeIndex],
                0,
                processCurrencyAmountForCountsData(transferValue)
            )
        },

        setCashDepositTransferSinceLastClose(state) {
            state.transferredToCashDepositSinceLastClose = true
        },

        setTransferToDepositViewActive(state, value) {
            state.transferToDepositViewActive = value
        },

        setTransferToDepositViewActiveAtIndex(state, data) {
            Vue.set(state.transferToDepositViewActive, data.index, data.value)
        },
    },
}
